// deps
import React, { memo, useMemo } from "react";

// internal-deps
import { format } from "@swiggy-private/core-ui";
import { getCloudinaryUrl, isEmpty, TEAM_LINE } from "@swiggy-private/js-utils";

// types
import { IStealDealStatusBanner } from "@dineout-payment-success-ui/interfaces";

// styles
import {
    StatusText,
    StatusBannerWrapper,
    TextContentWrapper,
    StyledImage,
    RedemptionInstructionSubtext,
    SubtextWrapper,
    BookingIdText,
    BgContainer,
} from "./StealDealStatusBanner.styles";

// constants
import {
    BOOKING_ID_TEXT,
    BOOKING_STATUS,
    STEAL_DEAL_STATUS_DETAILS as STATUS_DETAILS,
} from "@dineout-payment-success-ui/constants";

// helpers
import { getBookingStatusHelperText, getColorCode } from "@dineout-payment-success-ui/helpers/index";

const StealDealStatusBanner: React.FC<IStealDealStatusBanner> = ({
    bookingStatus,
    bookingId,
    bookingStatusText,
    bookingStatusBannerSubtitle,
    showBookingId = true,
    showBannerSubtitle = true,
}) => {
    const statusDetails = STATUS_DETAILS?.[bookingStatus] || {};
    const showStatusDescription = useMemo(() => bookingStatus === BOOKING_STATUS.PENDING, [bookingStatus]);
    const bgImageUrl = useMemo(
        () => getCloudinaryUrl(statusDetails.BACKGROUND || "", {}, { teamLine: TEAM_LINE.PORTAL }),
        [statusDetails.BACKGROUND],
    );
    const bookingStatusHelperText = getBookingStatusHelperText(true, bookingStatus);
    const titleText = useMemo(() => {
        if (bookingStatusText) return bookingStatusText;
        return format(statusDetails.TEXT ?? "", bookingStatusHelperText);
    }, [bookingStatusText, statusDetails.TEXT, bookingStatusHelperText]);

    return (
        <StatusBannerWrapper showStatusDescription={showStatusDescription}>
            <BgContainer>
                <StyledImage src={bgImageUrl} />
            </BgContainer>
            <TextContentWrapper>
                <StatusText variant={"V2.heading2.neutral.black"} colorKey={getColorCode(bookingStatus)}>
                    {titleText}
                </StatusText>
                <SubtextWrapper>
                    {showBookingId && (
                        <BookingIdText variant="V2.body3.neutral.bold" color="text_Low_Emphasis">
                            {format(BOOKING_ID_TEXT, bookingId || "")}
                        </BookingIdText>
                    )}
                    {showBannerSubtitle && !isEmpty(bookingStatusBannerSubtitle) && (
                        <RedemptionInstructionSubtext
                            variant="V2.body3.neutral.regular"
                            textColor={bookingStatusBannerSubtitle?.textColor}
                            colorKey="highlight"
                        >
                            {bookingStatusBannerSubtitle?.text}
                        </RedemptionInstructionSubtext>
                    )}
                </SubtextWrapper>
            </TextContentWrapper>
        </StatusBannerWrapper>
    );
};

export default memo(StealDealStatusBanner);
